import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { escapeRegExp } from './general';

const moment = extendMoment(Moment);

/**
 * This filters an array of card objects using the user-input text string,
 *
 * @param {object[]} cards - The full array of card objects.
 * @param {string} searchText - The search text to filter your cards arr on.
 *
 * @return {object[]} The filtered cards array.
 *
 * @example
 *
 *     filterCardsDataByTheirTextContent(cards, searchText)
 */
export function filterCardsDataByTheirTextContent(cards, searchText) {
  const filteredCards = cards.filter((cardDatum) => {
    const {
      title,
      img: imgPath,
      instructors,
      content,
    } = cardDatum;

    const searchRegExp = new RegExp(escapeRegExp(searchText), 'ig');

    const doesCardDataContainSearchStr = [
      title,
      imgPath,
      instructors,
      content,
    ].some((attr) => searchRegExp.test(attr));

    return doesCardDataContainSearchStr; // T/F
  });

  return filteredCards;
}

/**
 * This function sets a new radio button based on the user's click event.
 *
 * @param {string} firstDateToCheck - The first date we think is a string, i.e. '6/20/2021'
 * @param {string} secondDateToCheck - The second date we think is a string, i.e. '6/20/2021'
 *
 * @return {boolean} Whether or not the dates are set (i.e. they are not empty strings).
 *
 * @example
 *
 *     if (areDatesNOTSet(startingDate, endingDate))
 */
export function areDatesNOTSet(firstDateToCheck, secondDateToCheck) {
  return (firstDateToCheck === '' || secondDateToCheck === '');
}

/**
 * This function sets a new radio button based on the user's click event.
 *
 * @param {string} start - The starting date of the duration.
 * @param {string} end - The ending date of the duration.
 * @param {string} dateToTest - Another date, as a str.
 *
 * @return {boolean} Does dateToTest fall between the other 2 dates (inclusive)? T/F
 *
 * @example
 *
 *     return isDateWithinRange(startingDate, endingDate, startDate);
 */
export function isDateWithinRange(start, end, dateToTest) {
  return moment
    .range(
      moment(start, 'YYYY-MM-DD'),
      moment(end, 'YYYY-MM-DD')
    )
    .contains(moment(dateToTest, 'MM-DD-YYYY'));
}

/**
 * This filters the cards to display by finding if the start date of classes
 * is within the range selected.
 *
 * @param {object[]} cards - The array of card objects to filter.
 * @param {string} startingDate - The starting date of the duration. '6/20/2021'
 * @param {string} endingDate - The ending date of the duration. '6/20/2021'
 *
 * @return {boolean} Does dateToTest fall between the other 2 dates (inclusive)? T/F
 *
 * @example
 *
 *     filterCardsByStartDateWithinRange(cards, startingDate, endingDate)
 */
export function filterCardsByStartDateWithinRange(cards, startingDate, endingDate) {
  if (areDatesNOTSet(startingDate, endingDate)) {
    return cards;
  }

  return cards.filter((cardDatum) => {
    const { startDate } = cardDatum;

    return isDateWithinRange(startingDate, endingDate, startDate);
  });
}

/**
 * This converts Moment.js date objects into strings (=> '6/20/2021'), and feeds them & card data
 * info filterCardsByStartDateWithinRange(). This is primarily for the
 * Moment.js to string conversion.
 *
 * @param {object[]} datesPair - The start and end Moment.js date objs
 * @param {object[]} cardDataToFilter - The original cards array, that you will filter.
 *
 * @return {object[]} The filtered cards array.
 *
 * @example
 *
 *     filterCardsWithinDates(datesPair, cardDataToFilter)
 */
export function filterCardsWithinDates(datesPair, cardDataToFilter) {
  let [firstDate, secondDate] = datesPair;
  firstDate = firstDate.format('YYYY-MM-DD');
  secondDate = secondDate.format('YYYY-MM-DD');

  return filterCardsByStartDateWithinRange(
    cardDataToFilter,
    firstDate,
    secondDate,
  );
}

/**
 * This function filters cards by searching through their text, as well as their start dates.
 *
 * @param {(object[])} cardsDataToFilter - An array of cards data to filter.
 * @param {string} searchTxt - The user input string of text you should use to filter the cards.
 * @param {object[]} datesRangePair - The 2 Moment.js date objects to filter on.
 *
 * @example
 *
 *     filterCardsBySearchTxtAndDates(cardsData, value, dates);
 */
export function filterCardsBySearchTxtAndDates(cardsDataToFilter, searchTxt, datesRangePair) {
  const cardsDataFltrdBySearchTxt = filterCardsDataByTheirTextContent(cardsDataToFilter, searchTxt);
  return filterCardsWithinDates(datesRangePair, cardsDataFltrdBySearchTxt);
}

export function todaysDate() {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
  const yyyy = today.getFullYear();

  return mm + '/' + dd + '/' + yyyy;
}

export default {
  filterCardsDataByTheirTextContent,
  filterCardsWithinDates,
  areDatesNOTSet,
  isDateWithinRange,
  filterCardsByStartDateWithinRange,
  filterCardsBySearchTxtAndDates,
  todaysDate,
};
